import React, { useState } from "react";
import { Device, MachineCollection } from "../../../Types";
import CollectionListItem from "./CollectionListItem";
import CollectionsPickDialog from "./CollectionsPickDialog";
import ConfirmationModal from "../../ConfirmationModal";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSIconButton from "../../system-design/DSIconButton";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import firebaseService from "../../../firebase_service/firebaseService";

interface DeviceCollectionsWidgetProps {
  title: string;
  fieldName: "machineCollections" | "themedCollections";
  deviceId: string;
  data: Device;
  refetch: () => void;
}

function DeviceCollectionsWidget({
  title,
  fieldName,
  deviceId,
  data,
  refetch,
}: DeviceCollectionsWidgetProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const collections = data[fieldName]?.value || [];
  const itemsPerPage = 5;

  const handleRemoveCollection = (id: string) => {
    const updatedCollections = collections.filter((col) => col.id !== id);
    firebaseService
      .updateConfig(deviceId, fieldName, updatedCollections)
      .then(() => {
        refetch();
      });
  };

  const handleUpdateCollections = (
    selectedCollections: MachineCollection[],
  ) => {
    firebaseService
      .updateConfig(deviceId, fieldName, selectedCollections)
      .then(() => {
        refetch();
        setShowDialog(false);
      });
  };

  const handleRefresh = () => {
    firebaseService.updateConfig(deviceId, fieldName, collections);
  };

  const handleReset = () => {
    firebaseService.updateConfig(deviceId, fieldName, []).then(() => {
      refetch();
      setShowResetModal(false);
      setCurrentPage(0);
    });
  };

  return (
    <>
      <DSCard sx={{ pb: "15px", mb: 2, bgcolor: "background.paper" }}>
        <DSTypography variant="h6" sx={{ px: 2, pt: 2 }}>
          {`${title} ${collections.length > 0 ? `(${collections.length})` : ""}`}
        </DSTypography>
        <DSBox
          sx={{ borderBottom: "1px solid", borderColor: "divider", mt: 1 }}
        />
        <DSCardContent>
          {collections.length > 0 ? (
            <>
              {collections
                .slice(
                  currentPage * itemsPerPage,
                  (currentPage + 1) * itemsPerPage,
                )
                .map((collection) => (
                  <CollectionListItem
                    key={collection.id}
                    collectionId={collection.id}
                    isPermanent={collection.permanent}
                    isThemed={collection.themed}
                    canRemove={true}
                    onRemove={handleRemoveCollection}
                  />
                ))}
              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: "15px",
                  gap: 1,
                }}
              >
                <DSIconButton
                  onClick={() =>
                    setCurrentPage((prev: number) => Math.max(prev - 1, 0))
                  }
                  disabled={currentPage === 0}
                >
                  <NavigateBeforeIcon />
                </DSIconButton>
                <DSTypography>{currentPage}</DSTypography>
                <DSIconButton
                  onClick={() =>
                    setCurrentPage((prev: number) =>
                      Math.min(
                        prev + 1,
                        Math.ceil(collections.length / itemsPerPage) - 1,
                      ),
                    )
                  }
                  disabled={
                    currentPage ===
                    Math.ceil(collections.length / itemsPerPage) - 1
                  }
                >
                  <NavigateNextIcon />
                </DSIconButton>
              </DSBox>
            </>
          ) : (
            <DSBox sx={{ textAlign: "center" }}>
              No {title.toLowerCase()} added yet
            </DSBox>
          )}
          <DSBox
            sx={{ display: "flex", gap: "10px", mt: "15px", flexWrap: "wrap" }}
          >
            <DSPrimaryButton onClick={() => setShowDialog(true)}>
              Edit {title}
            </DSPrimaryButton>
            {collections.length > 0 && (
              <>
                <DSSecondaryButton
                  onClick={handleRefresh}
                  sx={{ minWidth: "100px" }}
                >
                  Refresh
                </DSSecondaryButton>
                <DSErrorButton
                  onClick={() => setShowResetModal(true)}
                  style={{ minWidth: "120px" }}
                >
                  Reset {title}
                </DSErrorButton>
              </>
            )}
          </DSBox>
        </DSCardContent>
      </DSCard>

      {showDialog && (
        <CollectionsPickDialog
          onClose={() => setShowDialog(false)}
          selectedCollections={collections}
          onPickCollections={handleUpdateCollections}
        />
      )}

      <ConfirmationModal
        open={showResetModal}
        message={`Are you sure you want to reset all ${title.toLowerCase()} for this device? This action will remove ${collections.length} collection(s) and cannot be undone.`}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
      />
    </>
  );
}

export default DeviceCollectionsWidget;
